import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, CalendarDays, Trash2, Users, Sun } from 'lucide-react';
import { remindersAPI, groupsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useSettings } from '../contexts/SettingsContext';
import { getRelativeTime, getRelativeTimeTranslated, sortRemindersByNextOccurrence, formatHijriFromStored, formatHijriDateDetailed, formatGregorianDateShort, formatPersianFromStored } from '../utils/dateHelpers';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';

const AllRemindersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const [reminders, setReminders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rems, grps] = await Promise.all([
        remindersAPI.getAll(),
        groupsAPI.getAll()
      ]);
      setReminders(sortRemindersByNextOccurrence([...(Array.isArray(rems) ? rems : [])]));
      setGroups(Array.isArray(grps) ? grps : []);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteReminder = (id) => {
    setReminderToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (reminderToDelete) {
      try {
        await remindersAPI.delete(reminderToDelete);
        setReminders(prev => prev.filter(r => r.id !== reminderToDelete));
      } catch (err) {
        console.error('Failed to delete:', err);
      }
      setShowDeleteModal(false);
      setReminderToDelete(null);
    }
  };

  const filteredReminders = reminders.filter(r => {
    if (filter === 'group') return !!r.groupId;
    if (filter !== 'all' && r.calendarType !== filter) return false;
    if (filterMonth || filterYear) {
      const d = new Date(r.date);
      if (filterMonth && (d.getMonth() + 1) !== parseInt(filterMonth)) return false;
      if (filterYear && d.getFullYear() !== parseInt(filterYear)) return false;
    }
    return true;
  });

  const getGroupName = (groupId) => {
    if (!groupId) return null;
    const group = groups.find(g => g.id === groupId);
    return group?.name || null;
  };

  const canDeleteReminder = (reminder) => {
    if (!reminder.groupId) return true;
    return reminder.createdBy === user?.id || !reminder.createdBy;
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate('/home')} data-testid="back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('all.title')}</h1>
          </div>

          {/* Filters — scrollable row */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide" data-testid="reminder-filter-tabs">
            {[
              { key: 'all',       label: `${t('all.all')} (${reminders.length})` },
              { key: 'gregorian', label: 'Gregorian' },
              { key: 'hijri',     label: 'Hijri' },
              { key: 'persian',   label: 'Shamsi' },
              { key: 'group',     label: `${t('all.groups')} (${reminders.filter(r => r.groupId).length})` },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFilter(key)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  filter === key
                    ? key === 'group' ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white text-slate-500 border border-slate-200'
                }`}>
                {label}
              </button>
            ))}
          </div>

          {/* Month/Year Filter */}
          <div className="flex gap-2 mb-6" data-testid="month-year-filter">
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} data-testid="filter-month"
              className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700">
              <option value="">{t('all.all_months')}</option>
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <option key={i+1} value={i+1}>{m}</option>
              ))}
            </select>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} data-testid="filter-year"
              className="w-28 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700">
              <option value="">{t('all.all_years')}</option>
              {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {(filterMonth || filterYear) && (
              <button onClick={() => { setFilterMonth(''); setFilterYear(''); }} data-testid="clear-date-filter"
                className="px-3 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-semibold">{t('all.clear')}</button>
            )}
          </div>

          {/* List */}
          {filteredReminders.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">
              <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('all.no_reminders')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReminders.map((reminder) => {
                const isHijri = reminder.calendarType === 'hijri';
                const isPersian = reminder.calendarType === 'persian';
                return (
                  <div key={reminder.id} className={`bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border overflow-hidden ${reminder.groupId ? 'border-teal-200' : 'border-slate-50'}`} data-testid="all-reminder-item">
                    {/* Shared indicator strip */}
                    {getGroupName(reminder.groupId) && (
                      <div className="flex items-center gap-1.5 px-4 py-1.5 bg-teal-50 border-b border-teal-100">
                        <Users size={11} className="text-teal-600" />
                        <span className="text-xs font-medium text-teal-700">{t('all.shared_via')} <span className="font-semibold">{getGroupName(reminder.groupId)}</span></span>
                      </div>
                    )}
                    <div className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isPersian ? 'bg-orange-100 text-orange-700' : isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {isPersian ? <Sun size={24} /> : isHijri ? <Moon size={24} /> : <CalendarDays size={24} />}
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/reminder/${reminder.id}`)}>
                        <h3 className="text-slate-900 font-semibold text-lg line-clamp-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{reminder.title}</h3>
                        <p className="text-slate-500 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {isPersian && reminder.persianDate
                            ? formatPersianFromStored(reminder.persianDate, lang)
                            : isHijri && reminder.hijriDate
                            ? formatHijriFromStored(reminder.hijriDate, lang)
                            : isHijri
                            ? formatHijriDateDetailed(new Date(reminder.date), lang)
                            : formatGregorianDateShort(new Date(reminder.date), lang)
                          }
                        </p>
                        {reminder.description && (
                          <p className="text-slate-400 text-sm mt-1 line-clamp-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{reminder.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {(() => {
                            const freq = reminder.repeatFrequency || (reminder.repeatYearly ? 'yearly' : 'yearly');
                            const hijriAdj = settings.hijriAdjustment || 0;
                            const relTime = getRelativeTime(reminder.date, reminder.repeatYearly, hijriAdj, reminder.hijriDate, reminder.calendarType, reminder.persianDate, freq);
                            const isToday = relTime === 'Today';
                            const isTomorrow = relTime === 'Tomorrow';
                            return (
                              <>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  isToday ? 'bg-slate-900 text-white'
                                  : isTomorrow ? isPersian ? 'bg-orange-100 text-orange-700' : isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                                  : 'bg-slate-100 text-slate-600'
                                }`}>{getRelativeTimeTranslated(reminder.date, reminder.repeatYearly, t, hijriAdj, reminder.hijriDate, reminder.calendarType, reminder.persianDate, freq)}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  freq === 'weekly' ? 'bg-amber-100 text-amber-700'
                                  : freq === 'monthly' ? 'bg-violet-100 text-violet-700'
                                  : freq === 'once' ? 'bg-slate-100 text-slate-500'
                                  : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {freq === 'once' ? t('reminder.one_time') : freq === 'weekly' ? t('reminder.weekly') : freq === 'monthly' ? t('reminder.monthly') : t('reminder.yearly')}
                                </span>
                              </>
                            );
                          })()}
                          {getGroupName(reminder.groupId) && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700 flex items-center gap-1">
                              <Users size={10} />{getGroupName(reminder.groupId)}
                            </span>
                          )}
                        </div>
                      </div>
                      {canDeleteReminder(reminder) && (
                        <button onClick={() => deleteReminder(reminder.id)} data-testid="delete-reminder-button"
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6" style={{ zIndex: 10000 }}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-sm rounded-3xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 size={32} className="text-red-600" /></div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('all.delete_title')}</h2>
              <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('all.delete_desc')}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold active:scale-95 transition-transform">{t('all.cancel')}</button>
              <button onClick={confirmDelete} data-testid="confirm-delete-button" className="flex-1 bg-red-600 text-white py-3 rounded-full font-semibold active:scale-95 transition-transform">{t('all.delete')}</button>
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default AllRemindersPage;
